/**
 * Tap fallback for when the vector cell layers are hidden under a raster or
 * OpenSeaMap-tiles base.
 *
 * The app always loads the ENC vector cells into a cache, but a raster/tiles
 * provider hides the cell layers (the base IS the chart there). So a tap's
 * queryRenderedFeatures finds nothing even where ENC data covers the area, and a
 * charted light returned no detail. This picks the nearest charted POINT object
 * from the cached cells, so tapping a light (beacon, buoy, landmark, structure…)
 * still opens its full detail over a raster or OpenSeaMap-seamark base.
 *
 * OpenSeaMap's own seamark tiles are transparent RASTER PNGs with no queryable
 * attributes, so they can't answer this on their own; this reads the ENC cache
 * underneath instead. Where there's no ENC coverage, there's genuinely nothing to
 * query and the caller falls through to the open-water readout.
 */
import type { ChartFeature, ChartFeatureCollection } from '@/types';

/**
 * Point objClasses that aren't tappable "things": soundings, quality/coverage
 * meta, and depth/sea areas (matched by the transit-snap skip list). Everything
 * else with a point geometry is a real charted object worth opening.
 */
export const NON_OBJECT_POINT_CLASSES: ReadonlySet<string> = new Set([
  'SOUNDG',
  'M_QUAL',
  'M_COVR',
  'DEPARE',
  'DEPCNT',
  'SEAARE',
]);

/**
 * Nearest charted POINT feature to a screen point across the cached cells, within
 * radiusPx, or undefined when nothing charted is close. `project` maps a
 * [lon, lat] to screen pixels (map.project in the app; a stub in tests).
 */
export function nearestCachedPointFeature(
  cells: Iterable<ChartFeatureCollection>,
  project: (lonlat: [number, number]) => { x: number; y: number },
  point: { x: number; y: number },
  radiusPx: number,
  skip: ReadonlySet<string> = NON_OBJECT_POINT_CLASSES,
): ChartFeature | undefined {
  let best: ChartFeature | undefined;
  let bestD = radiusPx;
  for (const fc of cells) {
    for (const ft of fc.features) {
      const g = ft.geometry;
      if (!g || g.type !== 'Point') continue;
      const oc = ft.properties?.objClass;
      if (typeof oc === 'string' && skip.has(oc)) continue;
      const xy = project(g.coordinates as [number, number]);
      const d = Math.hypot(xy.x - point.x, xy.y - point.y);
      if (d < bestD) {
        bestD = d;
        best = ft as ChartFeature;
      }
    }
  }
  return best;
}
